<template>
  <v-container fluid>
    <v-card class="elevation-1 mb-6">
      <v-card-title class="pa-7">
        Přehled nabídek
        <v-spacer></v-spacer>

        <v-btn
          color="primary"
          height="40px"
          min-width="130px"
          :to="{ name: 'personalized-offer.add' }"
          depressed
        >
          Nová nabídka
        </v-btn>
      </v-card-title>

      <v-card-text>
        <OffersFilters @filter="setFiltersAction" />

        <v-data-table
          v-model="offer.selected"
          :headers="userHeadersByRole"
          :items="offer.items"
          :options.sync="offer.options"
          :server-items-length="offer.serverItemsLength"
          :loading="offer.loading"
          item-key="id"
          class="elevation-0"
          show-select
        >
          <template v-slot:item.id="{ item }">
            <router-link
              :to="{
                name: 'personalized-offer.detail',
                params: {
                  offerId: item.id,
                },
              }"
            >
              {{ item.id }}
            </router-link>
          </template>
          <template v-slot:item.status="{ item }">
            <OfferStatus :status="item.status" />
          </template>
          <template v-slot:item.clientEnabled="{ item }">
            <EnabledDisabled :value="item.clientEnabled" />
          </template>
          <template v-slot:item.validSince="{ item }">
            <DateTime :disable-time="true" :datetime="item.validSince" />
          </template>
          <template v-slot:item.validUntil="{ item }">
            <DateTime :disable-time="true" :datetime="item.validUntil" />
          </template>
          <template v-slot:item.createdAt="{ item }">
            <DateTime :disable-time="false" :datetime="item.createdAt" />
          </template>
          <template v-slot:item.updatedAt="{ item }">
            <DateTime :disable-time="false" :datetime="item.updatedAt" />
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn-toggle color="primary" dense group>
              <v-btn
                :to="{ name: 'personalized-offer.edit', params: { offerId: item.id } }"
                color="blue-grey"
                text
              >
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
              <v-btn color="blue-grey" text @click="removeOffer(item.id)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-btn-toggle>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { setFilters } from '@/components/mixins/setFilters'
import { formatPhoneNumber } from '@/components/mixins/formatPhoneNumber'
import DateTime from '../../../components/DateTime'
import EnabledDisabled from '../../../components/EnabledDisabled'
import OffersFilters from '../../../components/filters/OffersFilters'
import OfferStatus from '../../../components/offer/OfferStatus'

export default {
  name: 'Offers',
  components: {
    OfferStatus,
    OffersFilters,
    EnabledDisabled,
    DateTime,
  },
  mixins: [setFilters, formatPhoneNumber],
  data() {
    return {
      offer: {
        selected: [],
        headers: [
          {
            text: 'Id',
            value: 'id',
            roles: ['ROLE_ADMIN'],
            width: '100px',
          },
          {
            text: 'Název',
            value: 'name',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Typ',
            value: 'type.text',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Stav',
            value: 'status',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Zákazník',
            value: 'clientEnabled',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Platnost od',
            value: 'validSince',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Platnost do',
            value: 'validUntil',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Vytvořeno',
            value: 'createdAt',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: 'Změněno',
            value: 'updatedAt',
            roles: ['ROLE_ADMIN'],
          },
          {
            text: '',
            value: 'actions',
            width: '1px',
            sortable: false,
            roles: ['ROLE_ADMIN'],
          },
        ],
        items: [],
        options: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['createdAt'],
          sortDesc: [true],
        },
        loading: false,
        serverItemsLength: 0,
      },
      offerFooter: {
        totalElements: 0,
        selectedData: {
          params: {},
          items: [],
        },
      },
      cancel: null,
    }
  },
  computed: {
    ...mapState({
      authorities: (state) => state.auth.user.authorities,
    }),
    userHeadersByRole() {
      return this.offer.headers.filter((e) => e.roles.some((e) => this.authorities.includes(e)))
    },
  },
  watch: {
    'offer.options': {
      handler() {
        this.loadOffers()
      },
    },
    '$route.query': {
      handler() {
        this.loadOffers()
      },
    },
    'offer.selected': {
      handler() {
        this.offerFooter.selectedData.items = this.offer.selected
      },
    },
  },
  methods: {
    ...mapActions({
      searchOffers: 'offer/searchOffers',
      deleteOffer: 'offer/deleteOffer',
    }),
    loadOffers() {
      this.offer.loading = true
      const searchParams = {
        filterParams: this.$route.query,
        options: this.offer.options,
      }
      this.offerFooter.selectedData.params = searchParams
      this.searchOffers(searchParams)
        .then((response) => {
          this.offerFooter.totalElements = response.data.totalElements
          this.offer.items = response.data.items
          this.offer.serverItemsLength = response.data.totalElements
          this.cancel = null
        })
        .finally(() => {
          this.offer.loading = false
        })
    },
    setFiltersAction(filters) {
      this.offer.options.page = 1
      this.setFilters(filters)
    },
    removeOffer(id) {
      this.$confirm('', {
        title: 'Smazat nabídku?',
        buttonTrueText: 'Ano',
        buttonFalseText: 'Ne',
        icon: '',
        color: 'black',
      }).then((result) => {
        if (result) {
          this.offer.loading = true
          this.deleteOffer(id)
            .then(() => {
              this.loadOffers()
            })
            .finally(() => {
              this.offer.loading = false
            })
        }
      })
    },
  },
}
</script>
